import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  CircularProgress,
  Paper,
  Alert,
  AlertTitle,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  LinearProgress,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  IconButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import {
  CloudUpload as CloudUploadIcon,
  CheckCircleOutline as CheckCircleOutlineIcon,
  ErrorOutline as ErrorOutlineIcon,
  GroupWork as GroupWorkIcon,
  Send as SendIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  Delete as DeleteIcon,
  ExpandMore as ExpandMoreIcon,
  Description as DescriptionIcon,
} from "@mui/icons-material";
import * as XLSX from "xlsx";
import { useBulkJoinCampaigns } from "../../../../client/hooks/products/promotions.mutations";
import {
  PROMOTION_TYPES,
  PROMOTION_TYPES_LABELS,
  getPayloadSpec,
  isKnownPromotionType,
} from "../../../../client/constants/promotionTypes";

const DEFAULT_PROMOTION_TYPE = "SMART";

// Valores aceitos na coluna "Ação" -> operação disparada no backend
const ACTION_ALIASES = {
  participar: "add",
  "deixar de participar": "remove",
  // sinônimos aceitos de planilhas antigas
  entrar: "add",
  aderir: "add",
  sair: "remove",
  remover: "remove",
};

const ACTION_LABELS = { add: "Participar", remove: "Deixar de Participar" };

// Normaliza acentos, caixa e espaços para casar "Ação"/"AÇÃO"/"acao" e "Deixar de Participar"/"deixar de  participar"
const normalizeText = (value) =>
  value
    ?.toString()
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/\s+/g, " ") || "";

const parseAction = (value) => ACTION_ALIASES[normalizeText(value)] || null;

// Busca a coluna ignorando acento, caixa e espaços: "Ação", "AÇÃO", "acao " caem no mesmo lugar
const pickColumn = (row, names) => {
  const wanted = names.map(normalizeText);
  const column = Object.keys(row).find((header) => wanted.includes(normalizeText(header)));
  return column ? row[column] : "";
};

const parseMoney = (value) => {
  const parsed = parseFloat(value?.toString().replace(",", "."));
  return isNaN(parsed) ? null : parsed;
};

const parseStock = (value) => {
  const parsed = parseInt(value?.toString().trim(), 10);
  return isNaN(parsed) ? null : parsed;
};

// O ML espera "2026-09-01T00:00:00" sem timezone; o Excel entrega Date, serial ou texto
const parseDateTime = (value) => {
  if (value === null || typeof value === "undefined" || value === "") return "";

  let date = null;
  if (value instanceof Date) {
    date = value;
  } else if (typeof value === "number") {
    // serial do Excel: dias desde 30/12/1899
    date = new Date(Date.UTC(1899, 11, 30) + value * 86400000);
  } else {
    const text = value.toString().trim();
    if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}$/.test(text)) return text;
    const parsed = new Date(text);
    date = isNaN(parsed.getTime()) ? null : parsed;
  }

  if (!date || isNaN(date.getTime())) return "";

  const pad = (number) => String(number).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
};

// Monta o objeto de items[] com os campos que o tipo da campanha aceita naquela operação
const buildItemPayload = (item) => {
  const spec = getPayloadSpec(item.promotionType, item.action) || {};
  const payload = { item_id: item.itemId };

  if (item.itemTitle) payload.item_title = item.itemTitle;
  if (spec.deal_price && item.dealPrice !== null) payload.deal_price = item.dealPrice;
  if (spec.top_deal_price && item.topDealPrice !== null) payload.top_deal_price = item.topDealPrice;
  if (spec.offer_id && item.offerId) payload.offer_id = item.offerId;
  if (spec.stock && item.stock !== null) payload.stock = item.stock;
  if (spec.start_date && item.startDate) payload.start_date = item.startDate;
  if (spec.finish_date && item.finishDate) payload.finish_date = item.finishDate;

  return payload;
};

const FIELD_LABELS = {
  deal_price: "Suggested Discounted Price",
  top_deal_price: "Top Deal Price",
  offer_id: "Offer Id",
  stock: "Stock",
  start_date: "Start Date",
  finish_date: "Finish Date",
};

// Descreve o spec de uma operação para a tabela de documentação
const describeSpec = (spec) => {
  if (spec === null) return "não se aplica";
  if (!spec) return "—";

  const byLevel = (level) =>
    Object.keys(spec)
      .filter((field) => spec[field] === level)
      .map((field) => FIELD_LABELS[field]);

  const parts = [];
  const required = byLevel("required");
  const recommended = byLevel("recommended");
  const optional = byLevel("optional");

  if (required.length) parts.push(`obrigatório: ${required.join(", ")}`);
  if (recommended.length) parts.push(`sugerido: ${recommended.join(", ")}`);
  if (optional.length) parts.push(`opcional: ${optional.join(", ")}`);

  return parts.length ? parts.join(" • ") : "só o Item ID";
};

// Consolida os grupos em contadores usados nos chips e no botão de processar
const summarizeGroups = (groups) => {
  const groupList = Object.values(groups);
  const countItems = (action) =>
    groupList
      .filter((group) => group.action === action)
      .reduce((sum, group) => sum + group.items.length, 0);

  return {
    campaigns: new Set(groupList.map((group) => group.campaignId)).size,
    requests: groupList.length,
    addItems: countItems("add"),
    removeItems: countItems("remove"),
    defaultTypes: groupList.filter((group) => !group.promotionTypeFromSheet).length,
  };
};

const AderirEmMassa = ({ open, onClose, onSuccess }) => {
  const [files, setFiles] = useState([]);
  const [previewData, setPreviewData] = useState([]);
  const [importStatus, setImportStatus] = useState("initial");
  const [error, setError] = useState(null);
  const [stats, setStats] = useState({ total: 0, valid: 0, invalid: 0, campaigns: 0, files: 0, requests: 0, addItems: 0, removeItems: 0, defaultTypes: 0 });
  const [visibleRows, setVisibleRows] = useState(50);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [campaignGroups, setCampaignGroups] = useState({});
  const [processingResults, setProcessingResults] = useState([]);
  const [currentProcessing, setCurrentProcessing] = useState(0);
  const [totalProcessing, setTotalProcessing] = useState(0);
  const [fileStats, setFileStats] = useState([]);
  const tableContainerRef = useRef(null);

  const bulkJoinMutation = useBulkJoinCampaigns();

  useEffect(() => {
    if (!open) {
      resetState();
    }
  }, [open]);

  const resetState = () => {
    setFiles([]);
    setPreviewData([]);
    setImportStatus("initial");
    setError(null);
    setStats({ total: 0, valid: 0, invalid: 0, campaigns: 0, files: 0, requests: 0, addItems: 0, removeItems: 0, defaultTypes: 0 });
    setVisibleRows(50);
    setIsLoadingMore(false);
    setCampaignGroups({});
    setProcessingResults([]);
    setCurrentProcessing(0);
    setTotalProcessing(0);
    setFileStats([]);
  };

  const loadMoreRows = useCallback(() => {
    if (isLoadingMore || visibleRows >= previewData.length) return;

    setIsLoadingMore(true);
    setTimeout(() => {
      setVisibleRows((prev) => Math.min(prev + 50, previewData.length));
      setIsLoadingMore(false);
    }, 300);
  }, [visibleRows, previewData.length, isLoadingMore]);

  const handleScroll = useCallback(
    (event) => {
      const { scrollTop, scrollHeight, clientHeight } = event.target;
      const threshold = 100;

      if (scrollHeight - scrollTop - clientHeight <= threshold) {
        loadMoreRows();
      }
    },
    [loadMoreRows]
  );

  const handleFileChange = (event) => {
    const selectedFiles = Array.from(event.target.files);
    if (selectedFiles.length > 0) {
      setImportStatus("uploading");
      
      // Processar todos os arquivos simultaneamente
      const filePromises = selectedFiles.map((file, index) => {
        return new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = (e) => {
            try {
              const data = e.target.result;
              const workbook = XLSX.read(data, { type: "array", cellDates: true });
              const sheetName = workbook.SheetNames[0];
              const worksheet = workbook.Sheets[sheetName];
              const jsonData = XLSX.utils.sheet_to_json(worksheet);

              const processedData = validateAndTransformData(jsonData, file.name, index);
              resolve({
                fileName: file.name,
                fileIndex: index,
                data: processedData,
                originalFile: file
              });
            } catch (err) {
              reject({ fileName: file.name, error: err });
            }
          };
          reader.onerror = () => reject({ fileName: file.name, error: "Erro ao ler arquivo" });
          reader.readAsArrayBuffer(file);
        });
      });

      Promise.allSettled(filePromises).then((results) => {
        const successfulFiles = [];
        const failedFiles = [];
        let consolidatedData = [];
        let consolidatedStats = [];

        results.forEach((result) => {
          if (result.status === 'fulfilled') {
            successfulFiles.push(result.value);
            consolidatedData = consolidatedData.concat(result.value.data);
            
            const fileValidItems = result.value.data.filter(item => item.isValid);
            const fileCampaigns = [...new Set(fileValidItems.map(item => item.campaignId))];
            
            consolidatedStats.push({
              fileName: result.value.fileName,
              total: result.value.data.length,
              valid: fileValidItems.length,
              invalid: result.value.data.length - fileValidItems.length,
              campaigns: fileCampaigns.length,
              addItems: fileValidItems.filter(item => item.action === "add").length,
              removeItems: fileValidItems.filter(item => item.action === "remove").length
            });
          } else {
            failedFiles.push(result.reason);
          }
        });

        if (successfulFiles.length === 0) {
          setError("Nenhum arquivo pôde ser processado com sucesso.");
          setImportStatus("error");
          return;
        }

        setFiles(successfulFiles);
        setFileStats(consolidatedStats);
        setPreviewData(consolidatedData);
        
        const validItems = consolidatedData.filter((item) => item.isValid);
        const campaignGroups = groupByCampaign(validItems);
        setCampaignGroups(campaignGroups);
        
        setStats({
          files: successfulFiles.length,
          total: consolidatedData.length,
          valid: validItems.length,
          invalid: consolidatedData.length - validItems.length,
          ...summarizeGroups(campaignGroups),
        });

        if (failedFiles.length > 0) {
          setError(`${failedFiles.length} arquivo(s) não puderam ser processados: ${failedFiles.map(f => f.fileName).join(', ')}`);
        }

        setImportStatus("preview");
      });
    }
  };

  const validateAndTransformData = (jsonData, fileName, fileIndex) => {
    return jsonData.map((row, index) => {
      const itemId = row["Item ID"] || row["item_id"] || row["id"] || row["ID"] || "";
      const campaignId = row["Campaign ID"] || row["campaign_id"] || row["campanha_id"] || row["CampaignID"] || "";
      const dealPrice = parseMoney(
        pickColumn(row, ["Suggested Discounted Price", "deal_price", "preco", "preço", "price"])
      );
      const topDealPrice = parseMoney(
        pickColumn(row, ["Top Deal Price", "top_deal_price", "Preço Top", "Preco Top"])
      );
      const offerId = pickColumn(row, ["Offer Id", "offer_id"])?.toString().trim() || "";
      const stock = parseStock(pickColumn(row, ["Stock", "Estoque", "Quantidade"]));
      const startDate = parseDateTime(pickColumn(row, ["Start Date", "start_date", "Data Inicio", "Data Início"]));
      const finishDate = parseDateTime(pickColumn(row, ["Finish Date", "finish_date", "Data Fim", "Data Termino", "Data Término"]));
      const itemTitle = pickColumn(row, ["Item Title", "item_title", "Title", "Titulo", "Título"])?.toString().trim() || "";
      const promotionTypeValue = pickColumn(row, ["Campaign Type", "campaign_type", "Promotion Type", "promotion_type", "Tipo", "Type"])?.toString().trim().toUpperCase() || "";
      const acaoValue = pickColumn(row, ["Ação", "Acao", "Action"]);

      const action = parseAction(acaoValue);
      const promotionType = promotionTypeValue || DEFAULT_PROMOTION_TYPE;
      // undefined = tipo desconhecido; null = o tipo não aceita essa operação
      const spec = action ? getPayloadSpec(promotionType, action) : undefined;

      let errors = [];
      if (!itemId) {
        errors.push("Item ID obrigatório");
      }
      if (!campaignId) {
        errors.push("Campaign ID obrigatório");
      }
      if (!action) {
        errors.push(`Coluna 'Ação' deve conter: ${ACTION_LABELS.add} ou ${ACTION_LABELS.remove}`);
      }
      if (!isKnownPromotionType(promotionType)) {
        // O back-end trata tipo desconhecido como DEAL silenciosamente, então barramos aqui
        errors.push(`Tipo de promoção inválido: ${promotionType}`);
      } else if (spec === null) {
        errors.push(`${promotionType} não aceita ${ACTION_LABELS[action]}`);
      } else if (spec) {
        // Cada tipo exige um conjunto diferente de campos em items[]
        if (spec.deal_price === "required" && !(dealPrice > 0)) {
          errors.push("Preço promocional obrigatório");
        }
        if (spec.stock === "required" && !(stock > 0)) {
          errors.push("Estoque reservado obrigatório");
        }
        if (spec.start_date === "required" && !startDate) {
          errors.push("Data de início obrigatória");
        }
        if (spec.finish_date === "required" && !finishDate) {
          errors.push("Data de término obrigatória");
        }
      }

      return {
        id: `${fileIndex}_${index}`,
        itemId: itemId?.toString().trim(),
        campaignId: campaignId?.toString().trim(),
        dealPrice: dealPrice,
        topDealPrice: topDealPrice,
        offerId: offerId,
        stock: stock,
        startDate: startDate,
        finishDate: finishDate,
        itemTitle: itemTitle,
        promotionType: promotionType,
        // Sem a coluna na planilha o tipo cai no default, que pode não bater com a campanha
        promotionTypeFromSheet: !!promotionTypeValue,
        acaoValue: acaoValue?.toString().trim() || "",
        action: action,
        isValid: errors.length === 0,
        errors: errors,
        sourceFile: fileName,
        fileIndex: fileIndex,
      };
    });
  };

  // Uma requisição por campanha + ação: a mesma campanha pode ter um bulk-add e um bulk-remove
  const groupByCampaign = (validItems) => {
    return validItems.reduce((groups, item) => {
      const key = `${item.campaignId}::${item.action}`;
      if (!groups[key]) {
        groups[key] = {
          campaignId: item.campaignId,
          action: item.action,
          promotionType: item.promotionType,
          promotionTypeFromSheet: item.promotionTypeFromSheet,
          items: [],
        };
      }
      groups[key].items.push(buildItemPayload(item));
      return groups;
    }, {});
  };

  const handleImport = async () => {
    if (stats.valid === 0) {
      setError("Nenhum item válido para processar.");
      return;
    }

    setImportStatus("processing");
    const campaignRequests = Object.values(campaignGroups).map((group) => ({
      campaignId: group.campaignId,
      action: group.action,
      promotionType: group.promotionType,
      items: group.items,
    }));

    setTotalProcessing(campaignRequests.length);
    setCurrentProcessing(0);

    try {
      const results = await bulkJoinMutation.mutateAsync({ campaignRequests });
      setProcessingResults(results);
      setImportStatus("completed");
      
      if (onSuccess) {
        onSuccess(results);
      }
    } catch (error) {
      setError(`Erro durante o processamento: ${error.message}`);
      setImportStatus("error");
    }
  };

  const getResultSummary = () => {
    if (processingResults.length === 0) return null;

    const successful = processingResults.filter(r => r.success).length;
    const failed = processingResults.filter(r => !r.success).length;
    const totalItems = processingResults.reduce((sum, r) => sum + r.itemCount, 0);

    return { successful, failed, totalItems };
  };

  const removeFile = (fileIndexToRemove) => {
    const updatedFiles = files.filter((_, index) => index !== fileIndexToRemove);
    const updatedFileStats = fileStats.filter((_, index) => index !== fileIndexToRemove);
    
    // Reprocessar dados sem o arquivo removido
    let consolidatedData = [];
    updatedFiles.forEach((file, newIndex) => {
      const updatedData = file.data.map(item => ({
        ...item,
        id: `${newIndex}_${item.id.split('_')[1]}`,
        fileIndex: newIndex
      }));
      consolidatedData = consolidatedData.concat(updatedData);
    });
    
    setFiles(updatedFiles);
    setFileStats(updatedFileStats);
    setPreviewData(consolidatedData);
    
    if (updatedFiles.length === 0) {
      resetState();
      return;
    }
    
    const validItems = consolidatedData.filter(item => item.isValid);
    const campaignGroups = groupByCampaign(validItems);
    setCampaignGroups(campaignGroups);
    
    setStats({
      files: updatedFiles.length,
      total: consolidatedData.length,
      valid: validItems.length,
      invalid: consolidatedData.length - validItems.length,
      ...summarizeGroups(campaignGroups),
    });
  };

  const addMoreFiles = (event) => {
    const newFiles = Array.from(event.target.files);
    if (newFiles.length === 0) return;
    
    setImportStatus("uploading");
    
    const currentFileCount = files.length;
    const filePromises = newFiles.map((file, index) => {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (e) => {
          try {
            const data = e.target.result;
            const workbook = XLSX.read(data, { type: "array", cellDates: true });
            const sheetName = workbook.SheetNames[0];
            const worksheet = workbook.Sheets[sheetName];
            const jsonData = XLSX.utils.sheet_to_json(worksheet);

            const processedData = validateAndTransformData(jsonData, file.name, currentFileCount + index);
            resolve({
              fileName: file.name,
              fileIndex: currentFileCount + index,
              data: processedData,
              originalFile: file
            });
          } catch (err) {
            reject({ fileName: file.name, error: err });
          }
        };
        reader.onerror = () => reject({ fileName: file.name, error: "Erro ao ler arquivo" });
        reader.readAsArrayBuffer(file);
      });
    });

    Promise.allSettled(filePromises).then((results) => {
      const successfulNewFiles = [];
      const failedFiles = [];

      results.forEach((result) => {
        if (result.status === 'fulfilled') {
          successfulNewFiles.push(result.value);
        } else {
          failedFiles.push(result.reason);
        }
      });

      if (successfulNewFiles.length === 0) {
        setError("Nenhum arquivo novo pôde ser processado.");
        setImportStatus("preview");
        return;
      }

      // Combinar com arquivos existentes
      const allFiles = [...files, ...successfulNewFiles];
      let consolidatedData = [];
      let consolidatedStats = [...fileStats];

      successfulNewFiles.forEach(file => {
        consolidatedData = consolidatedData.concat(file.data);
        
        const fileValidItems = file.data.filter(item => item.isValid);
        const fileCampaigns = [...new Set(fileValidItems.map(item => item.campaignId))];
        
        consolidatedStats.push({
          fileName: file.fileName,
          total: file.data.length,
          valid: fileValidItems.length,
          invalid: file.data.length - fileValidItems.length,
          campaigns: fileCampaigns.length,
          addItems: fileValidItems.filter(item => item.action === "add").length,
          removeItems: fileValidItems.filter(item => item.action === "remove").length
        });
      });

      // Adicionar dados existentes
      files.forEach(file => {
        consolidatedData = consolidatedData.concat(file.data);
      });

      setFiles(allFiles);
      setFileStats(consolidatedStats);
      setPreviewData(consolidatedData);
      
      const validItems = consolidatedData.filter(item => item.isValid);
      const campaignGroups = groupByCampaign(validItems);
      setCampaignGroups(campaignGroups);
      
      setStats({
        files: allFiles.length,
        total: consolidatedData.length,
        valid: validItems.length,
        invalid: consolidatedData.length - validItems.length,
        ...summarizeGroups(campaignGroups),
      });

      if (failedFiles.length > 0) {
        setError(`${failedFiles.length} arquivo(s) não puderam ser processados: ${failedFiles.map(f => f.fileName).join(', ')}`);
      }

      setImportStatus("preview");
    });
  };

  return (
    <Dialog
      open={open}
      onClose={importStatus !== "uploading" && importStatus !== "processing" ? onClose : undefined}
      maxWidth="lg"
      fullWidth
      keepMounted
    >
      <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <GroupWorkIcon sx={{ color: "#ff9800" }} />
        Aderir Em Massa às Campanhas
      </DialogTitle>

      <DialogContent>
        {importStatus === "initial" && (
          <>
            <Typography variant="body1" sx={{ mb: 2 }}>
              Importe uma ou múltiplas planilhas para participar ou deixar de participar de campanhas simultaneamente. 
              O formato esperado é uma tabela com as colunas:
            </Typography>

            <Box sx={{ mb: 3, p: 2, bgcolor: "grey.50", borderRadius: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                Colunas obrigatórias:
              </Typography>
              <ul style={{ margin: 0, paddingLeft: "20px" }}>
                <li><strong>Item ID</strong> - ID do produto no Mercado Livre</li>
                <li><strong>Campaign ID</strong> - ID da campanha</li>
                <li><strong>Ação</strong> - <strong>{ACTION_LABELS.add}</strong> ou <strong>{ACTION_LABELS.remove}</strong> (outras opções serão ignoradas)</li>
                <li><strong>Campaign Type</strong> - tipo da campanha (padrão: {DEFAULT_PROMOTION_TYPE}); define quais campos abaixo são exigidos</li>
              </ul>
              <Typography variant="body2" sx={{ fontWeight: 600, mt: 2, mb: 1 }}>
                Colunas que dependem do tipo da campanha:
              </Typography>
              <ul style={{ margin: 0, paddingLeft: "20px" }}>
                <li><strong>Suggested Discounted Price</strong> - preço promocional</li>
                <li><strong>Top Deal Price</strong> - preço para Mercado Pontos nível 3 a 6</li>
                <li><strong>Offer Id</strong> - id da oferta (<code>CANDIDATE-...</code> ao participar, <code>OFFER-...</code> ao sair)</li>
                <li><strong>Stock</strong> - estoque reservado para a promoção</li>
                <li><strong>Start Date</strong> / <strong>Finish Date</strong> - janela da promoção</li>
                <li><strong>Item Title</strong> - opcional, usado só na auditoria</li>
              </ul>
            </Box>

            <Accordion sx={{ mb: 3 }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  📑 Campos exigidos por tipo de campanha
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ p: 0 }}>
                <TableContainer sx={{ maxHeight: 320 }}>
                  <Table stickyHeader size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Tipo</TableCell>
                        <TableCell>{ACTION_LABELS.add}</TableCell>
                        <TableCell>{ACTION_LABELS.remove}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {PROMOTION_TYPES.map((promotionType) => (
                        <TableRow key={promotionType}>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontFamily: "monospace", fontWeight: 600 }}>
                              {promotionType}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {PROMOTION_TYPES_LABELS[promotionType]}
                            </Typography>
                          </TableCell>
                          <TableCell>{describeSpec(getPayloadSpec(promotionType, "add"))}</TableCell>
                          <TableCell>{describeSpec(getPayloadSpec(promotionType, "remove"))}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", p: 2 }}>
                  Além de <strong>Item ID</strong>, sempre obrigatório. Campos "sugeridos" podem ser omitidos —
                  o back-end resolve sozinho, mas cada item custa uma consulta a mais.
                </Typography>
              </AccordionDetails>
            </Accordion>

            <Alert severity="info" sx={{ mb: 3 }}>
              <AlertTitle>✨ Múltiplas Planilhas & Processamento Inteligente</AlertTitle>
              • Selecione várias planilhas de uma vez (Ctrl+Click)<br/>
              • O sistema consolida todos os dados automaticamente<br/>
              • Agrupamento inteligente por campanha e por ação<br/>
              • Processamento sequencial para garantir integridade<br/>
              • <strong>Ação = {ACTION_LABELS.add}:</strong> o item é adicionado à campanha<br/>
              • <strong>Ação = {ACTION_LABELS.remove}:</strong> o item é removido da campanha
            </Alert>

            <Box sx={{ mt: 3, mb: 2, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <Button
                variant="contained"
                component="label"
                startIcon={<CloudUploadIcon />}
                sx={{
                  bgcolor: "#ff9800",
                  "&:hover": { bgcolor: "#f57c00" },
                }}
              >
                Selecionar Planilhas
                <input
                  type="file"
                  hidden
                  multiple
                  accept=".xlsx,.xls,.csv"
                  onChange={handleFileChange}
                />
              </Button>
              
              <Typography variant="body2" color="text.secondary" sx={{ 
                display: 'flex', 
                alignItems: 'center',
                fontStyle: 'italic' 
              }}>
                💡 Use Ctrl+Click para selecionar múltiplos arquivos
              </Typography>
            </Box>
          </>
        )}

        {importStatus === "uploading" && (
          <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", flexDirection: "column", my: 4 }}>
            <CircularProgress size={50} sx={{ color: "#ff9800" }} />
            <Typography variant="body1" sx={{ mt: 2 }}>
              Processando o arquivo...
            </Typography>
          </Box>
        )}

        {importStatus === "preview" && (
          <>
            <Box sx={{ mb: 3 }}>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {stats.files} Planilha{stats.files > 1 ? 's' : ''} Carregada{stats.files > 1 ? 's' : ''}
                </Typography>
                <Button
                  variant="outlined"
                  component="label"
                  size="small"
                  startIcon={<CloudUploadIcon />}
                  sx={{
                    borderColor: "#ff9800",
                    color: "#ff9800",
                    "&:hover": { borderColor: "#f57c00", bgcolor: "rgba(255, 152, 0, 0.1)" },
                  }}
                >
                  Adicionar Mais
                  <input
                    type="file"
                    hidden
                    multiple
                    accept=".xlsx,.xls,.csv"
                    onChange={addMoreFiles}
                  />
                </Button>
              </Box>

              <Box sx={{ display: "flex", gap: 2, mb: 3, flexWrap: "wrap" }}>
                <Chip label={`📁 ${stats.files} arquivos`} variant="outlined" />
                <Chip label={`📊 Total: ${stats.total}`} variant="outlined" />
                <Chip label={`✅ Válidos: ${stats.valid}`} color="success" variant="outlined" />
                <Chip label={`🎯 Campanhas: ${stats.campaigns}`} sx={{ bgcolor: "#fff3e0", color: "#ff9800" }} />
                <Chip label={`➡️ ${ACTION_LABELS.add}: ${stats.addItems}`} color="success" variant="outlined" />
                <Chip label={`⬅️ ${ACTION_LABELS.remove}: ${stats.removeItems}`} color="warning" variant="outlined" />
                {stats.invalid > 0 && (
                  <Chip label={`❌ Inválidos: ${stats.invalid}`} color="error" variant="outlined" />
                )}
              </Box>

              {/* Gerenciador de Arquivos */}
              <Card sx={{ mb: 3 }}>
                <CardContent sx={{ p: 2 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                    📋 Gerenciar Arquivos
                  </Typography>
                  {fileStats.map((fileStat, index) => (
                    <Accordion key={index} sx={{ mb: 1, '&:last-child': { mb: 0 } }}>
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                          <DescriptionIcon color="primary" />
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="body1" sx={{ fontWeight: 500 }}>
                              {fileStat.fileName}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {fileStat.total} itens • {fileStat.valid} válidos • {fileStat.campaigns} campanhas • {fileStat.addItems} participar / {fileStat.removeItems} deixar de participar
                            </Typography>
                          </Box>
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              removeFile(index);
                            }}
                            sx={{ 
                              color: 'error.main',
                              '&:hover': { bgcolor: 'error.lighter' }
                            }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </AccordionSummary>
                      <AccordionDetails>
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                          <Chip size="small" label={`Total: ${fileStat.total}`} />
                          <Chip size="small" label={`Válidos: ${fileStat.valid}`} color="success" />
                          {fileStat.invalid > 0 && (
                            <Chip size="small" label={`Inválidos: ${fileStat.invalid}`} color="error" />
                          )}
                          <Chip size="small" label={`Campanhas: ${fileStat.campaigns}`} sx={{ bgcolor: "#fff3e0", color: "#ff9800" }} />
                          <Chip size="small" label={`${ACTION_LABELS.add}: ${fileStat.addItems}`} color="success" variant="outlined" />
                          <Chip size="small" label={`${ACTION_LABELS.remove}: ${fileStat.removeItems}`} color="warning" variant="outlined" />
                        </Box>
                      </AccordionDetails>
                    </Accordion>
                  ))}
                </CardContent>
              </Card>
            </Box>

            {stats.invalid > 0 && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                <AlertTitle>Atenção</AlertTitle>
                Há {stats.invalid} itens com dados inválidos que serão ignorados na importação.
              </Alert>
            )}

            {stats.defaultTypes > 0 && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                <AlertTitle>Tipo de promoção não informado</AlertTitle>
                {stats.defaultTypes} operação(ões) serão enviadas como <strong>{DEFAULT_PROMOTION_TYPE}</strong> porque
                a planilha não trouxe a coluna <strong>Campaign Type</strong>. O tipo define quais campos vão no
                payload — em uma campanha LIGHTNING, por exemplo, o estoque reservado seria descartado e a API
                devolveria erro. Preencha a coluna antes de processar.
              </Alert>
            )}

            <Box sx={{ display: "flex", gap: 2, mb: 3 }}>
              <Card sx={{ flex: 1 }}>
                <CardContent sx={{ p: 2 }}>
                  <Typography variant="h6" sx={{ color: "#ff9800", mb: 1 }}>
                    Resumo por Campanha
                  </Typography>
                  <List dense>
                    {Object.entries(campaignGroups).slice(0, 5).map(([groupKey, group]) => (
                      <ListItem key={groupKey} sx={{ px: 0 }}>
                        <ListItemIcon sx={{ minWidth: 32 }}>
                          <CheckCircleOutlineIcon
                            color={group.action === "remove" ? "warning" : "success"}
                            fontSize="small"
                          />
                        </ListItemIcon>
                        <ListItemText 
                          primary={`Campanha ${group.campaignId}`}
                          secondary={`${ACTION_LABELS[group.action]} • ${group.promotionType} • ${group.items.length} produtos`}
                          primaryTypographyProps={{ fontSize: "0.875rem" }}
                          secondaryTypographyProps={{ fontSize: "0.75rem" }}
                        />
                      </ListItem>
                    ))}
                    {Object.keys(campaignGroups).length > 5 && (
                      <Typography variant="body2" color="text.secondary" sx={{ pl: 2 }}>
                        +{Object.keys(campaignGroups).length - 5} operações adicionais
                      </Typography>
                    )}
                  </List>
                </CardContent>
              </Card>
            </Box>

            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Prévia dos dados ({Math.min(visibleRows, previewData.length)} de {previewData.length} itens):
            </Typography>

            <TableContainer
              component={Paper}
              sx={{ maxHeight: 400, mb: 2 }}
              ref={tableContainerRef}
              onScroll={handleScroll}
            >
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Arquivo</TableCell>
                    <TableCell>Item ID</TableCell>
                    <TableCell>Campaign ID</TableCell>
                    <TableCell>Tipo</TableCell>
                    <TableCell>Offer Id</TableCell>
                    <TableCell align="right">Preço Promocional</TableCell>
                    <TableCell align="center">Ação</TableCell>
                    <TableCell align="center">Status</TableCell>
                    <TableCell>Erros</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {previewData.slice(0, visibleRows).map((row) => (
                    <TableRow
                      key={row.id}
                      sx={{
                        backgroundColor: row.isValid ? "inherit" : "rgba(255, 0, 0, 0.05)",
                        "&:nth-of-type(odd)": {
                          bgcolor: (theme) => row.isValid ? theme.palette.action.hover : "rgba(255, 0, 0, 0.1)",
                        },
                      }}
                    >
                      <TableCell>
                        <Chip
                          label={row.sourceFile}
                          size="small"
                          variant="outlined"
                          sx={{ 
                            fontSize: "0.7rem",
                            maxWidth: "150px",
                            "& .MuiChip-label": {
                              overflow: "hidden",
                              textOverflow: "ellipsis"
                            }
                          }}
                        />
                      </TableCell>
                      <TableCell sx={{ fontFamily: "monospace" }}>
                        {row.itemId || "—"}
                      </TableCell>
                      <TableCell sx={{ fontFamily: "monospace" }}>
                        {row.campaignId || "—"}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontFamily: "monospace", fontSize: "0.75rem" }}>
                          {row.promotionType}
                        </Typography>
                        {!row.promotionTypeFromSheet && (
                          <Typography variant="caption" color="warning.main">
                            padrão
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell sx={{ fontFamily: "monospace" }}>
                        {row.offerId || "—"}
                      </TableCell>
                      <TableCell align="right">
                        {row.dealPrice !== null
                          ? `R$ ${row.dealPrice.toLocaleString("pt-BR", { minimumFractionDigits: 2 })}`
                          : "—"}
                        {(row.topDealPrice !== null || row.stock !== null || row.startDate) && (
                          <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                            {[
                              row.topDealPrice !== null ? `Top: R$ ${row.topDealPrice.toLocaleString("pt-BR", { minimumFractionDigits: 2 })}` : null,
                              row.stock !== null ? `Estoque: ${row.stock}` : null,
                              row.startDate ? `${row.startDate.split("T")[0]} → ${row.finishDate ? row.finishDate.split("T")[0] : "?"}` : null,
                            ].filter(Boolean).join(" • ")}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          label={row.acaoValue || "—"}
                          size="small"
                          color={row.action === "add" ? "success" : row.action === "remove" ? "warning" : "default"}
                          variant={row.action ? "filled" : "outlined"}
                          sx={{ 
                            fontSize: "0.75rem",
                            fontWeight: 500
                          }}
                        />
                      </TableCell>
                      <TableCell align="center">
                        {row.isValid ? (
                          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", color: "success.main" }}>
                            <CheckCircleOutlineIcon fontSize="small" sx={{ mr: 0.5 }} />
                            <Typography variant="body2">Válido</Typography>
                          </Box>
                        ) : (
                          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", color: "error.main" }}>
                            <ErrorOutlineIcon fontSize="small" sx={{ mr: 0.5 }} />
                            <Typography variant="body2">Inválido</Typography>
                          </Box>
                        )}
                      </TableCell>
                      <TableCell>
                        {row.errors.length > 0 && (
                          <Typography variant="body2" color="error">
                            {row.errors.join(", ")}
                          </Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}

                  {isLoadingMore && (
                    <TableRow>
                      <TableCell colSpan={9} align="center" sx={{ py: 2 }}>
                        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 1 }}>
                          <CircularProgress size={20} />
                          <Typography variant="body2" color="text.secondary">
                            Carregando mais itens...
                          </Typography>
                        </Box>
                      </TableCell>
                    </TableRow>
                  )}

                  {!isLoadingMore && visibleRows >= previewData.length && previewData.length > 50 && (
                    <TableRow>
                      <TableCell colSpan={9} align="center" sx={{ py: 1 }}>
                        <Typography variant="body2" color="text.secondary">
                          Todos os {previewData.length} itens foram carregados
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}

        {importStatus === "processing" && (
          <Box sx={{ my: 4 }}>
            <Typography variant="h6" sx={{ mb: 2, textAlign: "center" }}>
              Processando Campanhas...
            </Typography>
            <LinearProgress 
              variant="determinate" 
              value={(currentProcessing / totalProcessing) * 100} 
              sx={{ mb: 2, height: 8, borderRadius: 4 }}
            />
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: "center" }}>
              Processando operação {currentProcessing} de {totalProcessing}
            </Typography>
          </Box>
        )}

        {importStatus === "completed" && (
          <>
            <Alert severity="success" sx={{ mb: 3 }}>
              <AlertTitle>Processamento Concluído!</AlertTitle>
              {(() => {
                const summary = getResultSummary();
                return (
                  <Typography>
                    {summary.successful} de {processingResults.length} operações processadas com sucesso. 
                    Total de {summary.totalItems} produtos processados.
                  </Typography>
                );
              })()}
            </Alert>

            <Box sx={{ mb: 2 }}>
              <Typography variant="h6" sx={{ mb: 2 }}>Detalhes do Processamento:</Typography>
              <List>
                {processingResults.map((result, index) => (
                  <ListItem key={index} sx={{ px: 0 }}>
                    <ListItemIcon>
                      {result.success ? (
                        <CheckIcon color="success" />
                      ) : (
                        <CloseIcon color="error" />
                      )}
                    </ListItemIcon>
                    <ListItemText
                      primary={`Campanha ${result.campaignId} • ${ACTION_LABELS[result.action] || ACTION_LABELS.add}`}
                      secondary={
                        result.success
                          ? `${result.itemCount} produtos ${result.action === "remove" ? "removidos" : "adicionados"} com sucesso`
                          : `Erro: ${result.error} (${result.itemCount} produtos)`
                      }
                    />
                  </ListItem>
                ))}
              </List>
            </Box>
          </>
        )}

        {importStatus === "error" && (
          <Alert severity="error" sx={{ my: 2 }}>
            <AlertTitle>Erro</AlertTitle>
            {error}
          </Alert>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        {importStatus === "initial" && (
          <Button onClick={onClose} sx={{ color: "text.secondary" }}>
            Cancelar
          </Button>
        )}

        {importStatus === "preview" && (
          <>
            <Button onClick={onClose} sx={{ color: "text.secondary" }}>
              Cancelar
            </Button>
            <Button onClick={resetState} sx={{ color: "text.secondary" }}>
              Selecionar outro arquivo
            </Button>
            <Button
              variant="contained"
              startIcon={<SendIcon />}
              onClick={handleImport}
              disabled={stats.valid === 0 || bulkJoinMutation.isPending}
              sx={{
                bgcolor: "#ff9800",
                color: "white",
                fontWeight: 600,
                "&:hover": { bgcolor: "#f57c00" },
              }}
            >
              Processar {stats.requests} operações
            </Button>
          </>
        )}

        {(importStatus === "completed" || importStatus === "error") && (
          <Button 
            onClick={onClose} 
            variant="contained"
            sx={{
              bgcolor: "#ff9800",
              "&:hover": { bgcolor: "#f57c00" },
            }}
          >
            Fechar
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default AderirEmMassa;